import {makeAutoObservable} from 'mobx';

import {i18n} from '@yourcoach/i18n';

import type {CourseExpandedCommon} from '../../../../api/course';
import type {CollectionStore} from '../../../../api/utils/collectionStore';
import type {MethodStatus} from '../../../../api/utils/entityStore';
import type {
  Destructible,
  WithBusyManager,
  WithReactionDispose,
} from '../../../../utils/store';
import type {CoachCourseGroupId, CoursesGroupContract} from '../../types';

import {courseExpandCommon, courseStore} from '../../../../api/course';
import {editionStore, programStore} from '../../../../api/program';
import {currentUserStore} from '../../../../api/user';
import {createCollectionStore} from '../../../../api/utils/collectionStore';
import {alert} from '../../../../components/alert';
import {courseDelete} from '../../../../services/entity-action/course/course.delete';
import {courseStart} from '../../../../services/entity-action/course/course.start';
import {courseStop} from '../../../../services/entity-action/course/course.stop';
import {courseUpgrade} from '../../../../services/entity-action/course/course.upgrade';
import {CancelRequestError} from '../../../../utils/errors/app';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '../../../../utils/eventEmitter';
import {logger} from '../../../../utils/logger';
import {platform} from '../../../../utils/platform';
import {
  AsyncBusyManager,
  ReactionDisposeManager,
} from '../../../../utils/store';

type Config = {
  disableAutoFetch?: boolean;
  programId: string;
};

const ACTIVE_COURSES_LIMIT = 200;
const ARCHIVED_COURSES_LIMIT = 20;

type ReasonKey = 'deleteCourse' | 'startCourse' | 'stopCourse' | 'updateCourse';

export class CoachCoursesStore
  implements Destructible, WithBusyManager<ReasonKey>, WithReactionDispose
{
  busy = new AsyncBusyManager<ReasonKey>();
  disposer = new ReactionDisposeManager();
  private readonly activeCoursesStore: CollectionStore<CourseExpandedCommon>;
  private readonly archivedCoursesStore: CollectionStore<CourseExpandedCommon>;

  constructor(private readonly config: Config) {
    this.activeCoursesStore = createCollectionStore<CourseExpandedCommon>({
      method: 'coach.courses.list',
      params: {
        expand: courseExpandCommon,
        limit: ACTIVE_COURSES_LIMIT,
        query: [
          ['program_id', '==', config.programId],
          ['status', '!=', 'archived'],
          [
            'hidden_for_ids',
            '!contains',
            currentUserStore.user?._id ? [currentUserStore.user._id] : [],
          ],
        ],
        sort: [['start_date', -1]],
      },
      withCount: true,
    });

    this.archivedCoursesStore = createCollectionStore<CourseExpandedCommon>({
      method: 'coach.courses.list',
      params: {
        expand: courseExpandCommon,
        limit: ARCHIVED_COURSES_LIMIT,
        query: [
          ['program_id', '==', config.programId],
          ['status', '==', 'archived'],
          [
            'hidden_for_ids',
            '!contains',
            currentUserStore.user?._id ? [currentUserStore.user._id] : [],
          ],
        ],
        sort: [['start_date', -1]],
      },
    });

    this.busy.observe(this, {
      deleteCourse: 'deleteCourse',
      startCourse: 'startCourse',
      stopCourse: 'stopCourse',
      updateCourse: 'updateCourse',
    });

    makeAutoObservable(this, {}, {autoBind: true});

    this.setupReactions();

    emitter.on(WS_RECEIVE_MESSAGE_EVENT, this.handleWsMessage);

    if (!config.disableAutoFetch) {
      this.fetch();
    }
  }

  destruct() {
    this.activeCoursesStore.clear();
    this.archivedCoursesStore.clear();
    this.disposer.dispose();
    emitter.off(WS_RECEIVE_MESSAGE_EVENT, this.handleWsMessage);
  }

  get activeCoursesNumber() {
    return this.activeCoursesStore.count;
  }

  get coursesGroupedByStatus(): CoursesGroupContract<CoachCourseGroupId>[] {
    const activeCourses = this.activeCoursesStore.items;
    const archivedCourses = this.archivedCoursesStore.items;

    return [activeCourses, archivedCourses]
      .filter(courses => courses.length)
      .map(courses => {
        const isArchived = courses[0]!.status === 'archived';
        const isTitleNeeded = isArchived || platform.isWeb;

        return {
          courses,
          id: isArchived ? 'archived' : 'active',
          title: isTitleNeeded
            ? isArchived
              ? i18n.t('shared:archived')
              : i18n.t('shared:active')
            : undefined,
        };
      });
  }

  get hasMore() {
    return this.archivedCoursesStore.hasMore;
  }

  get isActionInProgress() {
    return (
      this.busy.getReason('startCourse') ||
      this.busy.getReason('stopCourse') ||
      this.busy.getReason('deleteCourse') ||
      this.busy.getReason('updateCourse')
    );
  }

  get isEmpty() {
    return this.activeCoursesStore.isEmpty && this.archivedCoursesStore.isEmpty;
  }

  get isFetching() {
    return (
      this.activeCoursesStore.isFetching || this.archivedCoursesStore.isFetching
    );
  }

  async deleteCourse(course: CourseExpandedCommon) {
    logger.event('group_delete_tap', {screen: 'program'});

    try {
      await courseDelete({entity: course, params: {}});
    } catch (e) {
      alert.error(e);
    }
  }

  async fetch(silent = false) {
    try {
      await Promise.all([
        this.activeCoursesStore.fetch(
          {
            limit: Math.max(
              this.activeCoursesStore.count,
              ACTIVE_COURSES_LIMIT,
            ),
          },
          {silent},
        ),
        this.archivedCoursesStore.fetch(
          {
            limit: Math.max(
              this.archivedCoursesStore.count,
              ARCHIVED_COURSES_LIMIT,
            ),
          },
          {silent},
        ),
      ]);
    } catch (error) {
      if (error instanceof CancelRequestError) {
        return;
      }

      throw error;
    }
  }

  async fetchMore() {
    if (this.archivedCoursesStore.hasMore) {
      try {
        await this.archivedCoursesStore.fetch(
          {
            offset: this.archivedCoursesStore.items.length,
          },
          {
            silent: true,
          },
        );
      } catch (e) {
        // Do nothing
      }
    }
  }

  async startCourse(course: CourseExpandedCommon) {
    try {
      logger.event('start_program_tap', {program_type: 'group'});
      await courseStart({entity: course, params: {}});
    } catch (e) {
      // Do nothing
    }
  }

  async stopCourse(course: CourseExpandedCommon) {
    try {
      await courseStop({entity: course, params: {}});
    } catch (e) {
      alert.error(e);
    }
  }

  async updateCourse(course: CourseExpandedCommon) {
    try {
      logger.event('update_group_conditions_tap');
      await courseUpgrade({entity: course, params: {}});

      this.fetch(true);
    } catch (e) {
      alert.error(e);
    }
  }

  private handleWsMessage(message: any) {
    if (
      [
        'course_created',
        'course_paused',
        'course_pending',
        'course_resumed',
        'course_started',
        'course_stopped_restarting',
        'edition_created',
        'edition_signed',
        'membership_accepted',
        'membership_created',
      ].includes(message.event.type)
    ) {
      this.fetch(true);
    }
  }

  private setupReactions() {
    this.disposer.watchMany<MethodStatus<unknown>>({
      condition: status => status.success,
      effect: () => this.fetch(true),
      reactionConfigs: [
        {expression: () => programStore.updating, key: 'updatingProgram'},
        {expression: () => editionStore.creating, key: 'creatingEdition'},
        {expression: () => editionStore.updating, key: 'updatingEdition'},
        {expression: () => editionStore.deleting, key: 'deletingEdition'},
        {expression: () => courseStore.creating, key: 'creatingCourse'},
      ],
    });

    this.disposer.watchMany({
      condition: status => status.success,
      effect: (arg, prev) => {
        if (arg.entity) {
          this.activeCoursesStore.updateItem(
            arg.entity as CourseExpandedCommon,
            arg.entity,
          );
        } else {
          this.activeCoursesStore.removeItem(
            prev.entity as CourseExpandedCommon,
          );
        }

        this.fetch(true);
      },
      reactionConfigs: [
        {expression: () => courseStore.updating, key: 'updatingCourse'},
        {expression: () => courseStore.deleting, key: 'deletingCourse'},
      ],
    });
  }
}
